import { editRoutine } from '../domain/training.ts';
import type { RoutineExerciseRef } from '../domain/training.ts';
import type { TrainingRepository, RoutineView, CreateRoutineCommand } from '../ports/training-repository.ts';
import { intent } from './training-intent.ts';
export type { RoutineView } from '../ports/training-repository.ts';
export interface RoutineFields { name: string; exercises: readonly RoutineExerciseRef[] }
type Port = Pick<TrainingRepository, 'readContext' | 'listRoutines' | 'readRoutine' | 'readRoutineRevision' | 'createRoutine' | 'editRoutine' | 'archiveRoutine' | 'readExerciseRevision'>;
export class TrainingRoutines {
  constructor(private readonly repository: Port, private readonly id: () => string) {}
  listRoutines() { return this.repository.listRoutines(); }
  getRoutine(id: string) { return this.repository.readRoutine(id); }
  getRoutineRevision(ref: string) { return this.repository.readRoutineRevision(ref); }
  async resolveExercises(refs: readonly RoutineExerciseRef[]) {
    const results = await Promise.all([...refs].sort((a, b) => a.order - b.order).map(ref => this.repository.readExerciseRevision(ref.exerciseRevisionRef)));
    const failed = results.find(r => !r.ok); if (failed && !failed.ok) return failed;
    return { ok: true as const, value: results.flatMap(r => r.ok ? [r.value] : []) };
  }
  createRoutine(input: RoutineFields) {
    const draft = structuredClone(input);
    return intent<CreateRoutineCommand, RoutineView>(async () => {
      const context = await this.repository.readContext(); if (!context.ok) return context;
      const routineId = this.id(); const routineRevisionId = this.id();
      return { ok: true, value: { operationId: this.id(), context: context.value,
        routine: { routineId, archived: false, currentRevisionRef: routineRevisionId },
        revision: { ...draft, routineId, routineRevisionId, revision: 1 } } };
    }, c => this.repository.createRoutine(c), c => this.getRoutine(c.routine.routineId));
  }
  editRoutine(view: RoutineView, input: RoutineFields) {
    const observed = structuredClone(view); const draft = structuredClone(input);
    return intent(async () => {
      const result = editRoutine(observed.routine, observed.revision, { ...draft,
        routineRevisionId: this.id(), expectedRevision: observed.revision.revision });
      if (!result.ok) return result;
      return { ok: true, value: { operationId: this.id(), context: observed.context, routineId: observed.routine.routineId,
        expectedRevision: observed.revision.revision, nextRevision: result.value.revision } };
    }, c => this.repository.editRoutine(c), c => this.getRoutine(c.routineId));
  }
  archiveRoutine(view: RoutineView) {
    const observed = structuredClone(view);
    return intent(async () => ({ ok: true, value: { operationId: this.id(), context: observed.context,
      routineId: observed.routine.routineId, expectedRevision: observed.revision.revision } }),
    c => this.repository.archiveRoutine(c), c => this.getRoutine(c.routineId));
  }
}
